import React, { useState } from "react";
import { toast } from "react-toastify";
import { queryEmail } from "../services/apiService";
import { ArrowRight } from "lucide-react";

const Sections = () => {
  const [sent, setSent] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const categories = [
    {
      id: "software",
      title: "Software Development Services",
      subtitle:
        "Build resilient, scalable software tailored to your business needs.",
      items: [
        {
          title: "Custom Web Application Development",
          desc: "End-to-end web apps built with modern stacks, optimized for performance and maintainability.",
        },
        {
          title: "Mobile App Development (iOS / Android)",
          desc: "Native and cross-platform mobile apps focused on user experience and reliability.",
        },
        {
          title: "Enterprise Software Solutions",
          desc: "Robust systems for complex workflows — integrations, security, and scale.",
        },
        {
          title: "API Development & Integration",
          desc: "Design and build secure, versioned APIs and connect 3rd-party services seamlessly.",
        },
        {
          title: "E-Commerce Platform Development",
          desc: "Custom storefronts, checkout flows, and integrations for a conversion-first experience.",
        },
        {
          title: "SaaS Product Development",
          desc: "From MVP to production SaaS: multitenancy, billing, observability, and product design.",
        },
        {
          title: "UI/UX Design & Prototyping",
          desc: "Human-centered design, interactive prototypes, and design systems for consistency.",
        },
        {
          title: "Software Modernization & Refactoring",
          desc: "Upgrade legacy systems, reduce technical debt, and improve developer velocity.",
        },
      ],
    },
    {
      id: "web",
      title: "Web & Digital Services",
      subtitle:
        "Stand out online with pixel-perfect websites and data-driven digital marketing.",
      items: [
        {
          title: "Website Design & Development",
          desc: "Custom websites with fast load times, responsive layouts and modern best practices.",
        },
        {
          title: "WordPress / CMS Development",
          desc: "Flexible CMS solutions for content-driven sites, with custom themes & plugins.",
        },
        {
          title: "Search Engine Optimization (SEO)",
          desc: "Technical and content SEO to improve visibility and organic traffic.",
        },
        {
          title: "Digital Marketing & Branding",
          desc: "Campaign strategy, creatives and analytics to grow awareness and leads.",
        },
        {
          title: "UI/UX Design & Research",
          desc: "User research, wireframes, and visual design that increase engagement.",
        },
        {
          title: "Performance Optimization",
          desc: "Speed audits, image optimization, and front-end tuning for better UX and SEO.",
        },
        {
          title: "Website Maintenance",
          desc: "Ongoing updates, security patches, backups and monitoring to keep sites healthy.",
        },
      ],
    },
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (name.trim() == "" || email.trim() == "" || message.trim() == "") {
      toast.warning("Please fill out the contact us form !");
      return false;
    }

    const payload = {
      name: name,
      email: email,
      subject: "New Enquiry From " + name + " : Casual Tech",
      message: message,
    };

    try {
      let res = await queryEmail("public/api/v1/contact", payload);
      if (res?.data?.success) {
        console.log("res", res);
        toast.success(res?.data?.message);
        setSent(true);
        setTimeout(() => setSent(false), 5000);
        e.target.reset();
      }
    } catch (error) {
      toast.error("Something went wrong, please try again.");
      console.log("Error during sending mail - ", error);
      return false;
    }
  };

  return (
    <div className="sections">
      {/* Welcome short intro (kept minimal because Hero is main) */}
      <section id="home" className="section pt-4">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-12">
              <div className="hero-content p-4 rounded-3">
                <h4 className="display-6 fw-bold">
                  We build reliable software & cloud solutions
                </h4>
                <p className="lead text-muted">
                  Casual Tech delivers web, mobile and cloud services with
                  strong emphasis on maintainability, performance and clear
                  SLAs. From prototype to production — we handle it all.
                </p>

                <div className="d-flex gap-2 flex-wrap">
                  <a href="/start" className="btn btn-primary btn-lg">
                    Build Your Website
                  </a>
                  <a
                    href="/dashboard"
                    className="btn btn-outline-secondary btn-lg"
                  >
                    Login
                  </a>
                </div>
              </div>
              <div className="mt-3 small text-muted">
                <strong>Trusted by:</strong> startups, agencies & SMBs — India &
                global.
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* About */}
      <section id="about" className="section bg-white border-top">
        <div className="container">
          <div className="row g-4">
            <div className="col-md-12">
              <h3>About Casual Tech</h3>
              <p className="text-muted">
                <strong>Casual Tech</strong> is a smart, fast, and user-friendly
                website-building platform designed to help individuals and
                businesses create their professional online presence
                effortlessly. With our{" "}
                <strong>single-form website generator</strong>, customers simply
                submit their business details—such as name, logo, preferred
                language, contact information, pages, and product/service
                listings—and our system builds a complete website within hours.
              </p>
              <p className="text-muted">
                Customers can choose from multiple modern templates, search and
                book a domain, make payments securely, and receive access to a
                <strong>personal dashboard</strong>
                to update or manage their website content anytime. We also offer
                an integrated <strong>Communication/Support</strong> module,
                allowing users to raise support tickets and get quick responses
                from our team.
              </p>
              <p className="text-muted">
                At Casual Tech, our mission is to make website creation
              </p>
              <ul className="text-muted">
                <li>Simple</li>
                <li>Fast</li>
                <li>Affordable and accessible</li>
              </ul>
              <p className="text-muted">for everyone.</p>

              <h4>
                <strong>Casual Tech</strong> workflow diagram.
              </h4>
              <img
                src="/assets/img/how-casual-tech-works.png"
                className="how-casual-tech-works"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="section pt-4">
        <div className="container">
          <div className="container py-5">
            <header className="text-center mb-5">
              <h1 className="display-5 fw-bold">Our IT Services</h1>
              <p className="lead text-muted">
                Solutions that cover product, web, and digital experiences —
                delivered with speed and quality.
              </p>
            </header>

            {categories.map((cat) => (
              <section key={cat.id} className="mb-5">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div>
                    <h2 className="h4">{cat.title}</h2>
                    <p className="text-muted mb-0">{cat.subtitle}</p>
                  </div>
                </div>

                <div className="row g-3">
                  {cat.items.map((item) => (
                    <div key={item.title} className="col-12 col-sm-6 col-lg-3">
                      <div className="card h-100 shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <h3 className="h6 card-title">{item.title}</h3>
                          <p className="card-text text-muted small">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      {/* Return Policy */}
      <section id="return-policy" className="section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <h4>Return Policy</h4>
              <p className="text-muted">
                Casual Tech provides digital services and does not offer
                physical products. Once a service is delivered—such as website
                generation, template setup, domain booking, or dashboard access—
                <strong>returns are not applicable</strong>.{" "}
              </p>
              <p className="text-muted">
                However, if a user encounters an issue or believes a mistake
                occurred during website creation, they may raise a ticket
                through the <strong>Support/Communication</strong> module, and
                our team will assist in resolving the issue promptly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Refund Policy */}
      <section id="refund-policy" className="section bg-white border-top">
        <div className="container">
          <h4>Refund Policy</h4>
          <p>
            Refunds at Casual Tech are only applicable under the following
            conditions:{" "}
          </p>
          <p>
            <strong>
              1. Payment was completed but the customer did not receive any
              service
            </strong>
            , such as website creation or dashboard access.
          </p>{" "}
          <p>
            <strong>2. Duplicate payments</strong> due to technical errors.
          </p>
          <p>
            <strong>3. Domain booking failure</strong> due to unavailability
            after payment (refund or alternative domain option will be offered).
          </p>
          <p className="text-muted">
            Refunds are <strong>not applicable for:</strong>
          </p>
          <ul className="text-muted">
            <li>Successful website creation</li>
            <li>Dashboard access</li>
            <li>Template updates or content updates</li>
            <li>Services already rendered</li>
          </ul>
          <p>
            All refund requests must be initiated through the customer’s
            dashboard via the Support/Communication section.
          </p>
        </div>
      </section>

      {/* Privacy Policy */}
      <section id="privacy-policy" className="section">
        <div className="container">
          <h4>Privacy Policy</h4>
          <p>
            Casual Tech is committed to protecting your privacy.
            <br />
            We collect only the information necessary to create and maintain
            your website, including business name, logo, contact details, pages,
            domain preferences, and product/service data.
          </p>
          <p>Your information is used solely for:</p>
          <ul>
            <li>
              <p>Website creation</p>
            </li>
            <li>
              <p>Secure account & dashboard access</p>
            </li>
            <li>
              <p>Communication & support</p>
            </li>
            <li>
              <p>Service improvement</p>
            </li>
          </ul>
          <p>
            We <strong>do not sell or share</strong> personal data with third
            parties, except for essential integrations like domain registration
            or payment gateways.
            <br />
            All data is stored securely, and users can request modification or
            deletion of their data at any time through the support module.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section id="disclaimer" className="section bg-white border-top">
        <div className="container">
          <h4>Disclaimer</h4>
          <p className="text-muted">
            Casual Tech provides tools and services to help users build and
            manage their websites. While we make every effort to ensure accuracy
            and uptime, we do not guarantee:{" "}
          </p>
          <ul>
            <li>Continuous uninterrupted service</li>
            <li>Accuracy of user-provided content</li>
            <li>Availability of specific domain names</li>
            <li>Outcomes of website performance, SEO, or traffic</li>
          </ul>
          <p className="text-muted">
            All website content is created based on the information entered by
            the customer. Casual Tech is not responsible for errors, omissions,
            or misuse of this information.
          </p>
        </div>
      </section>
      {/* TNC */}
      <section id="tnc" className="section bg-white border-top">
        <div className="container">
          <h4>Terms & Conditions</h4>
          <ol>
            <li>
              <p>
                By using Casual Tech services, you agree to provide accurate
                business information.
              </p>
            </li>
            <li>
              <p>
                All website content generated is based on user-submitted data;
                Casual Tech is not liable for inaccuracies.
              </p>
            </li>
            <li>
              <p>
                Payment must be completed before website creation, domain
                booking, and template deployment.
              </p>
            </li>
            <li>
              <p>
                Dashboard access is granted for managing content; misuse or
                harmful activities may result in suspension.
              </p>
            </li>
            <li>
              <p>
                Domain names are subject to availability at the time of
                purchase.
              </p>
            </li>
            <li>
              <p>
                Refunds are provided only under the conditions listed in our
                Refund Policy.
              </p>
            </li>
            <li>
              <p>
                Users must not upload harmful, illegal, or copyrighted content
                without permission.
              </p>
            </li>
            <li>
              <p>
                Casual Tech may update or modify these terms at any time to
                improve service quality.
              </p>
            </li>
          </ol>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-7">
              <div className="card p-3">
                <h4>Contact Us</h4>
                <p className="text-muted">
                  We typically respond within one business day.
                </p>

                <form onSubmit={handleContactSubmit} className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input
                      name="name"
                      className="form-control"
                      required
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      required
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Message</label>
                    <textarea
                      name="message"
                      rows="6"
                      className="form-control"
                      required
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <button className="btn btn-primary">Send Message</button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary ms-2"
                      onClick={() =>
                        window.scrollTo({ top: 0, behavior: "smooth" })
                      }
                    >
                      Back to top
                    </button>
                  </div>
                  {sent && (
                    <div className="col-12">
                      <div className="alert alert-success">
                        Message sent — thanks!
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="card p-3">
                <h6>Contact Info</h6>
                <p className="text-muted mb-1">
                  <strong>Email:</strong> info@casualtech.cloud
                </p>
                <p className="text-muted mb-1">
                  <strong>Phone:</strong> +91 9718-050-999
                </p>
                <p className="text-muted mb-1">
                  <strong>Address:</strong> A-167, Noida Sector 122
                </p>
                <hr />
                <h6>Hours</h6>
                <p className="text-muted mb-0">Mon — Fri: 9:00 — 18:00 (IST)</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sections;
